import Plot from 'react-plotly.js';
import * as RO from './ReportObjects.js';

interface new_zone{
    name: string;
    hours: number;
}
interface zone_plot{
    x_value: [];
    y_value: [];
}
export function createLineChart(
    datasets, // Array of { name: string, x: [], y: [], color: string }
    plotName,
    xyLabels = [{name: "", tickAngle: 45}, {name: "", rangemode: "tozero"}],
    markerSize = 6,
    showMarkersText = [true, true],
    plotMargin = [100, 0, 0, 100],
    plotSize = [1100, 280],
    chartTitleParam = ["", "#000", 8],
    plotLegend = [0, 0]
) {
    let datasetsArray = datasets;
    if(typeof datasets == "object") datasetsArray = Array.isArray(datasets) ? datasets : Object.values(datasets); // Convert object to array of datasets

    let traces = datasetsArray.map(dataset => ({
        x: dataset.x,
        y: dataset.y,
        mode: showMarkersText[0] ? "lines+markers+text" : "lines",
        name: dataset.name || "",
        marker: { color: dataset.color || RO.line_plot_colours[dataset.index] },
        text: showMarkersText[1] ? dataset.y.map(String) : [],
        textposition: 'top center',
        type: dataset.type,
        line: { color: dataset.color || RO.line_plot_colours[dataset.index], width: 2 }
    }));
    let yMin = Math.min(...datasetsArray.map(dataset => Math.min(...dataset.y)));
    let yMax = Math.max(...datasetsArray.map(dataset => Math.max(...dataset.y)));
    let buffer = (yMax - yMin) * 0.15;

    let xaxis_json = {
         title: xyLabels[0].name,
         tickangle: xyLabels[0].tickAngle,
         tickfontsize:1,
         tickpad: 10,
         automargin: true,
         ticks: 'outside'
    }
    if(xyLabels[0].tickfontsize) xaxis_json.tickfontsize = xyLabels[0].tickfontsize;
    if(xyLabels[0].tickpad) xaxis_json.tickpad = xyLabels[0].tickpad;
    if(xyLabels[0].tickvals) xaxis_json.tickvals = xyLabels[0].tickvals;
    if(xyLabels[0].tickmode) xaxis_json.tickmode = xyLabels[0].tickmode;
    if(xyLabels[0].tickmode) xaxis_json.ticktest = xyLabels[0].ticktest;
    if(xyLabels[0].automargin) xaxis_json.automargin = xyLabels[0].automargin;
    if(xyLabels[0].ticks) xaxis_json.ticks = xyLabels[0].ticks;
    if(xyLabels[0].range) xaxis_json.range = xyLabels[0].range;
    if(xyLabels[0].title) xaxis_json.range = xyLabels[0].title;

    let yaxis_json = {
         title: xyLabels[1].name,
         range: [0, yMax + buffer],
         rangemode: xyLabels[1].rangemode,
         automargin: true,
    }

    if(xyLabels[1].tickfontsize) yaxis_json.tickfontsize = xyLabels[1].tickfontsize;
    if(xyLabels[1].tickpad) yaxis_json.tickpad = xyLabels[1].tickpad;
    if(xyLabels[1].tickvals) yaxis_json.tickvals = xyLabels[1].tickvals;
    if(xyLabels[1].tickmode) yaxis_json.tickmode = xyLabels[1].tickmode;
    if(xyLabels[1].ticktest) yaxis_json.ticktest = xyLabels[1].ticktest;
    if(xyLabels[1].automargin) yaxis_json.automargin = xyLabels[1].automargin;
    if(xyLabels[1].ticks) yaxis_json.automargin = xyLabels[1].ticks;
    if(xyLabels[1].range) yaxis_json.range = xyLabels[1].range;
    if(xyLabels[1].title) yaxis_json.range = xyLabels[1].title;

    let layout = {
        autosize: false,
        autorange: false,

        title: {
            text: chartTitleParam[0],
            font: {
                size: chartTitleParam[1],
                color: chartTitleParam[2]
            }
        },
        xaxis: xaxis_json,
        yaxis: yaxis_json,
        width: plotSize[0],
        height: plotSize[1],
        margin: { l: plotMargin[0], r: plotMargin[1], t: plotMargin[2], b: plotMargin[3], },
        legend: { x: 1, y: 1 },
    };

    const plot_object: RO.AllZonePlots = {
        plot_name: plotName,
        plot_data: { data:traces, layout }
    }

    return plot_object;
}

export function createBarChart(
    datasets,
    plotName,
    xyLabels = [{ name: "", tickAngle: 45, showticklabels: false }, { name: "", rangemode: "tozero", showticklabels:false }],
    label_size= 8,
    showText = true,
    plotMargin = [100, 0, 0, 100],
    plotSize = [1100, 280],
    chartTitleParam = ["", "#000", 8]
) {
    let datasetsArray = datasets;
    if (Array.isArray(datasets)) datasetsArray = datasets;
    else if (typeof datasets === "object" && datasets !== null) datasetsArray = [datasets];

    let traces = datasetsArray.map((dataset, index) => ({
        x: [dataset.x],
        y: [dataset.y],
        type: "bar",
        name: truncateLabel(dataset.name || ""),
        marker: { color: dataset.color || RO.line_plot_colours[index] },
        text: showText[0] ? Array.isArray(dataset.y) ? dataset.y.map(String) : dataset.y ? String(dataset.y) : "" : "",
        textposition: 'auto',
        textfont: { size: label_size },
        hovertemplate: '%{label}: %{value} hours',
    }));

    let layout = {
        autosize: false,
        title: { text: chartTitleParam[0], font: { size: chartTitleParam[1], color: chartTitleParam[2] } },
        xaxis: { title: xyLabels[0].name, tickangle: xyLabels[0].tickAngle, automargin: true, showticklabels: false},
        yaxis: { title: xyLabels[1].name, rangemode: xyLabels[1].rangemode, automargin: true, showticklabels: true},
        width: plotSize[0],
        height: plotSize[1],
        margin: { l: plotMargin[0], r: plotMargin[1], t: plotMargin[2], b: plotMargin[3] },
        barmode: "group",
        legend: getLegendLayout(datasetsArray, label_size),
    };

    const plot_object: RO.AllZonePlots = {
        plot_name: plotName,
        plot_data: { data: traces, layout }
    };

    return plot_object;
}

export function createPieChart(
    datasets, // Array of { name: string, labels: string[], values: number[], color?: string | string[] }
    plotName: string,
    chartTitleParam = ["", "#000", 8],
    label_size = 8,
    plotSize = [400, 300],
    plotMargin = [40, 0, 40, 0]
) {
    let datasetsArray = datasets;
    if (Array.isArray(datasets)) datasetsArray = datasets;
    else if (typeof datasets === "object" && datasets !== null) datasetsArray = [datasets];

    const traces = datasetsArray.map((dataset, index) => ({
        type: "pie",
        labels: dataset.y,
        values: dataset.x,
        name: dataset.name,
        marker: { colors: Array.isArray(dataset.color) ? dataset.color : undefined },
        textinfo: "label+percent",
        textposition: "inside",
        hovertemplate: '%{label}: %{value} hours (%{percent})<extra></extra>',
        hole: 0, // use >0 for donut
        textfont: { size: label_size },
        insidetextfont: { size: label_size }
    }));

    const layout = {
        autosize: false,
        title: {
            text: chartTitleParam[0],
            font: { size: chartTitleParam[1], color: chartTitleParam[2] }
        },
        width: plotSize[0],
        height: plotSize[1],
        margin: { l: plotMargin[0], r: plotMargin[1], t: plotMargin[2], b: plotMargin[3] },
        showlegend: false,
        legend: { font: { size: label_size } }
    };

    const plot_object: RO.AllZonePlots = {
        plot_name: plotName,
        plot_data: { data: traces, layout }
    };

    return plot_object;
}


function getLegendLayout(data, label_size) {
    const maxItemsInColumn = 14;

    if (data.length > maxItemsInColumn) {
        return {
            orientation: 'h',
            x: 1,
            y: 1,
            xanchor: 'left',
            itemwidth: 40,
            font: { size: label_size },
        };
    } else {
        return {
            orientation: 'v',
            x: 1,
            y: 1,
            xanchor: 'left',
            itemwidth: 60,
            font: { size: label_size },
        };
    }
}
function truncateLabel(label, length = 20) {
  const clean = label.length > length ? label.slice(0, length - 1) + '…' : label;
  return clean.padEnd(length, ' ');
}
